import { getItemById, createOptimizedUtils, getItemCheckedState } from './helpers';
import { TreeCheckboxBaseNode, TreeItemsMap } from './types';

/**
 * 체크된 ID들을 정규화하여 최적화된 체크 목록 반환
 * - 하위 아이템들이 모두 체크된 경우 상위 아이템으로 통합
 * - 중복된 ID들 제거 및 불필요한 하위 ID들 정리
 */
function normalizeCheckedIds<T extends TreeCheckboxBaseNode>(
  checkedIds: string[],
  itemsMap: TreeItemsMap<T>,
  cacheUtils: ReturnType<typeof createOptimizedUtils<T>>,
): string[] {
  const normalizedIds = new Set(checkedIds);
  const candidateParents = new Set<T>();

  checkedIds.forEach((id) => {
    const item = getItemById(id, itemsMap);
    if (item) {
      const ancestorIds = cacheUtils.getAncestors(item.id);
      ancestorIds.forEach((ancestorId) => {
        const ancestor = getItemById(ancestorId, itemsMap);
        if (ancestor) {
          candidateParents.add(ancestor);
        }
      });
    }
  });

  Array.from(candidateParents)
    .filter((item) => item.children?.length)
    .forEach((parent) => {
      const enabledChildren = parent.children!.filter((child: TreeCheckboxBaseNode) => !child.disabled);

      if (enabledChildren.length === 0) return;

      const allEnabledChildrenChecked = enabledChildren.every(
        (child: TreeCheckboxBaseNode) => getItemCheckedState(child as T, normalizedIds).checked,
      );

      if (allEnabledChildrenChecked) {
        normalizedIds.add(parent.id);
        // 하위 항목들 제거 (상위로 통합)
        cacheUtils.getEnabledDescendants(parent).forEach((id) => normalizedIds.delete(id));
      }
    });

  return Array.from(normalizedIds);
}

/**
 * 체크 해제 작업이 필요한지 확인
 */
function shouldProcessUncheck<T extends TreeCheckboxBaseNode>(
  item: T,
  itemWithDescendants: string[],
  checkedIds: Set<string>,
  itemsMap: TreeItemsMap<T>,
  cacheUtils: ReturnType<typeof createOptimizedUtils<T>>,
): boolean {
  const hasDirectItems = itemWithDescendants.some((id) => checkedIds.has(id));
  const hasCheckedParent = Array.from(checkedIds).some((checkedId) => {
    const checkedItem = getItemById(checkedId, itemsMap);
    return checkedItem && cacheUtils.hasDescendant(checkedItem, item.id);
  });

  return hasDirectItems || hasCheckedParent;
}

/**
 * 특정 아이템들을 제외하고 부모의 자식들을 체크 목록에 추가
 */
function addChildrenExceptRemoved<T extends TreeCheckboxBaseNode>(
  parent: T,
  excludeIds: Set<string>,
  checkedIds: Set<string>,
  cacheUtils: ReturnType<typeof createOptimizedUtils<T>>,
): void {
  if (!parent.children?.length) return;

  parent.children.forEach((child) => {
    const typedChild = child as T;

    // disabled 자식은 제외
    if (excludeIds.has(typedChild.id) || typedChild.disabled) return;

    if (!typedChild.children?.length) {
      checkedIds.add(typedChild.id);
      return;
    }

    const hasExcludedDescendant = Array.from(excludeIds).some((excludeId) =>
      cacheUtils.hasDescendant(typedChild, excludeId),
    );

    if (hasExcludedDescendant) {
      addChildrenExceptRemoved(typedChild, excludeIds, checkedIds, cacheUtils);
    } else {
      checkedIds.add(typedChild.id);
    }
  });
}

/**
 * 해제할 아이템을 포함하는 상위 항목들을 분해하여 나머지 자식들을 개별 체크
 */
function decomposeParentsContainingItem<T extends TreeCheckboxBaseNode>(
  targetItem: T,
  removedIds: string[],
  checkedIds: Set<string>,
  itemsMap: TreeItemsMap<T>,
  cacheUtils: ReturnType<typeof createOptimizedUtils<T>>,
): void {
  if (!removedIds.length || !checkedIds.size) return;

  const excludeIds = new Set(removedIds);
  const processedIds = new Set<string>();

  Array.from(checkedIds).forEach((checkedId) => {
    if (processedIds.has(checkedId)) return;

    const checkedItem = getItemById(checkedId, itemsMap);
    if (!checkedItem) {
      processedIds.add(checkedId);
      return;
    }

    if (cacheUtils.hasDescendant(checkedItem, targetItem.id)) {
      checkedIds.delete(checkedId);
      processedIds.add(checkedId);
      addChildrenExceptRemoved(checkedItem, excludeIds, checkedIds, cacheUtils);
    } else {
      processedIds.add(checkedId);
    }
  });
}

/**
 * 체크/해제 로직을 처리하고 정규화된 체크 ID 배열 반환
 * - 아이템 체크 시: 아이템과 모든 하위 아이템들을 체크 목록에 추가
 * - 아이템 해제 시: 아이템과 하위들 제거 + 관련 상위 아이템들 분해 처리
 */
export function processItemCheck<T extends TreeCheckboxBaseNode>(
  item: T,
  checked: boolean,
  checkedIds: string[],
  itemsMap: TreeItemsMap<T>,
): string[] {
  const newCheckedIds = new Set(checkedIds);
  // 캐싱 유틸리티를 한 번만 생성하고 모든 함수에서 공유
  const cacheUtils = createOptimizedUtils<T>(itemsMap);
  const itemWithDescendants = [item.id, ...cacheUtils.getEnabledDescendants(item)];

  if (checked) {
    if (!item.disabled) {
      newCheckedIds.add(item.id);
    }
    cacheUtils.getEnabledDescendants(item).forEach((id) => {
      newCheckedIds.add(id);
    });
  } else {
    // 해제: 처리가 필요한지 먼저 확인
    if (!shouldProcessUncheck(item, itemWithDescendants, newCheckedIds, itemsMap, cacheUtils)) {
      return checkedIds;
    }
    // 아이템과 하위 아이템 제거
    itemWithDescendants.forEach((id) => newCheckedIds.delete(id));
    // 상위 항목들 분해 처리
    if (newCheckedIds.size > 0) {
      decomposeParentsContainingItem(item, itemWithDescendants, newCheckedIds, itemsMap, cacheUtils);
    }
  }

  return normalizeCheckedIds(Array.from(newCheckedIds), itemsMap, cacheUtils);
}
